import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Checkbox, FormControlLabel, FormGroup, Switch, ToggleButton, ToggleButtonGroup, useMediaQuery } from '@mui/material';
import {
  ReactFlow, ReactFlowProvider, Controls, ControlButton, MiniMap, Panel,
  applyNodeChanges, applyEdgeChanges, useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './ImpactGraph.css';
import ImpactGraphNode from './nodes/ImpactGraphNode';
import { usePref } from '../../services/prefs';
import {
  collectLinkTypes, buildGraphModel, layoutFamilyTree, layoutVModel, toReactFlowGraph,
  EDGE_COLORS, ROOT_COLOR,
} from './impactGraphLayout';

// Impact GRAPH view (#182 rebuild) — a backwards-and-forwards connected
// "family tree" (or, toggled, a V-model) rendering of the same
// upstream/downstream/verification data ImpactTab's list view already fetches
// (GET /items/{id}/impact-tree). Rendering/layout is now @xyflow/react (React
// Flow) + @dagrejs/dagre (see `impactGraphLayout.js` for the layered-graph
// layout itself) — React Flow supplies pan/zoom/minimap/controls/edges
// natively instead of the previous hand-rolled SVG/div implementation.
//
// Every node reuses ItemTooltip + IdChip (via the custom `impactNode` type,
// see `nodes/ImpactGraphNode.js`), unchanged, so the rich hover tooltip and
// id-display prefs (#174) work identically to the list view.

const nodeTypes = { impactNode: ImpactGraphNode };

// Fixed viewport/behaviour defaults — deliberately not fighting React Flow's
// own good defaults for pan/zoom.
// Item 10 ("default view should be 'fit all'"): `fitView` + `fitViewOptions`
// below is what makes the initial render show the WHOLE graph rather than a
// fixed zoom level — React Flow computes whatever zoom fits the content
// within `minZoom`/`maxZoom` and applies it on mount, and the effect in
// `ImpactGraphCanvas` re-runs the same fit whenever the layout changes (filter/
// titles/layout-mode toggle), so it's never a stale/fixed zoom. MIN_ZOOM is
// generous specifically so a large graph can still shrink enough to fit
// entirely rather than being clamped before it's fully visible.
const FIT_VIEW_OPTIONS = { padding: 0.2, duration: 0 };
const MIN_ZOOM = 0.15;
const MAX_ZOOM = 2;
// The minimap is an "orientation aid past ~30 nodes" per the original design
// ask — for a small graph it adds nothing (the whole thing already fits in
// view) and, worse, its fixed corner position can end up sitting on top of a
// real node once fitView has zoomed in tight. Gate it on node count instead
// of always rendering it.
const MINIMAP_NODE_THRESHOLD = 24;
// Shared mobile breakpoint (matches `screens.css`'s `@media (max-width: 640px)`
// convention used elsewhere in the app, e.g. issue #201) — drives both the
// MiniMap's JS-set `style={{width,height}}` (item 12, can't be resized by CSS
// alone since @xyflow/react also sizes its internal SVG viewBox from that
// same style prop) and the touch-pan/scroll trade-off (item 15, below).
const MOBILE_QUERY = '(max-width:640px)';
const MINIMAP_SIZE = { width: 200, height: 150 };
const MINIMAP_SIZE_MOBILE = { width: 96, height: 72 };

function minimapNodeColor(n) {
  return (n.data && n.data.color) || ROOT_COLOR;
}

// A small inline "expand" icon for the fullscreen ControlButton — the app's
// shared icon set (`components/shell/Icons.jsx`) has no fullscreen glyph, and
// React Flow's own Controls buttons are themselves plain inline SVGs, so this
// matches that convention rather than growing the shared icon set for one use.
function ExpandIcon() {
  // `.react-flow__controls-button svg { fill: currentColor }` (ImpactGraph.css,
  // matching how the built-in zoom/fitview icons are drawn as filled glyphs)
  // would otherwise override this stroke-only icon's `fill="none"` attribute —
  // an inline `style` wins over that external rule, so it's set here rather
  // than as a bare attribute.
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" style={{ fill: 'none' }} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}

// Inner canvas — split out so it can call `useReactFlow()` (needs to be inside
// a `<ReactFlowProvider>`) to re-fit the view smoothly whenever the layout is
// recomputed (layout-mode / filter / titles-toggle change), not just on mount.
function ImpactGraphCanvas({ rfGraph, reduceMotion, isMobile, fullscreenRef }) {
  const { fitView } = useReactFlow();
  const [nodes, setNodes] = useState(rfGraph.nodes);
  const [edges, setEdges] = useState(rfGraph.edges);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Item 15, done properly this time (mobile pan/pinch vs page-scroll-trap):
  // you cannot have "one finger scrolls the page" AND "one finger pans the
  // graph" — it's the same gesture — so the graph is PASSIVE on mobile until
  // the user taps to activate it (the embedded-Google-Maps pattern). Passive:
  // page scrolls through freely (no trap), React Flow ignores touch. Active:
  // React Flow's full native pan+pinch (its default behaviour), and a "Done"
  // affordance releases back to scroll mode. Desktop and fullscreen are always
  // interactive (a mouse has no scroll-vs-pan ambiguity; fullscreen has no page
  // left to scroll). This replaces the previous `panOnDrag={!isMobile}` which
  // — because @xyflow/system's gesture filter rejects EVERY touchstart when
  // panOnDrag is false — left mobile with NO pan and NO pinch at all (the exact
  // "pinch zoom and pan don't work" report).
  const [touchActive, setTouchActive] = useState(false);
  const interactive = !isMobile || touchActive || isFullscreen;

  // `reduceMotion` is read for the fit ANIMATION only, at the moment the rAF fires —
  // it must not be a re-run trigger, or toggling the pref would re-seed nodes/edges and
  // jump the viewport for a change that only affects how the NEXT fit is animated.
  // Held in a ref so the latest value is used without widening the trigger set.
  const reduceMotionRef = useRef(reduceMotion);
  useEffect(() => { reduceMotionRef.current = reduceMotion; }, [reduceMotion]);
  // `fitView` is safe to depend on: `useReactFlow()` builds it in a `useMemo` keyed on
  // the (stable) store, so its identity does not change between renders.
  useEffect(() => {
    setNodes(rfGraph.nodes);
    setEdges(rfGraph.edges);
    const raf = requestAnimationFrame(() => {
      fitView({ ...FIT_VIEW_OPTIONS, duration: reduceMotionRef.current ? 0 : 350 });
    });
    return () => cancelAnimationFrame(raf);
  }, [rfGraph, fitView]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // In passive mobile mode, a two-finger gesture would otherwise trigger the
  // BROWSER's native page pinch-zoom (the app's viewport meta allows user
  // scaling, and an element's `touch-action` can't fully suppress page zoom) —
  // which is the "it zoomed the whole app" behaviour reported earlier. Swallow
  // multi-touch moves over the canvas until the user opts into interaction;
  // single-finger (page scroll) is left untouched. Non-passive listener so
  // preventDefault actually takes effect.
  useEffect(() => {
    if (interactive) return undefined;
    const el = fullscreenRef.current;
    if (!el) return undefined;
    const blockMultiTouch = (e) => { if (e.touches && e.touches.length > 1) e.preventDefault(); };
    el.addEventListener('touchmove', blockMultiTouch, { passive: false });
    return () => el.removeEventListener('touchmove', blockMultiTouch);
  }, [interactive, fullscreenRef]);

  const onNodesChange = useCallback((changes) => setNodes((nds) => applyNodeChanges(changes, nds)), []);
  const onEdgesChange = useCallback((changes) => setEdges((eds) => applyEdgeChanges(changes, eds)), []);

  // Item 14 ("full screen view button") — Fullscreen API on the OUTER canvas
  // container (not just the React Flow pane) so the toolbar/toggles above it
  // stay reachable; falls back to a no-op (button still renders, just does
  // nothing) on browsers/embeds without Fullscreen API support rather than
  // throwing.
  const toggleFullscreen = useCallback(() => {
    const el = fullscreenRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else if (el.requestFullscreen) {
      el.requestFullscreen();
    }
  }, [fullscreenRef]);

  return (
    <ReactFlow
      className={`impact-graph-flow${reduceMotion ? ' ig-reduce-motion' : ''}${isMobile && !interactive ? ' ig-touch-scroll' : ''}`}
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={nodeTypes}
      // Item 4 ("moving nodes doesn't make sense"): this is a derived/computed
      // dagre layout, not something a user should rearrange. The per-node
      // `draggable: false` set in `toReactFlowGraph` was NOT sufficient on its
      // own (verified via a real drag in a live browser — the node moved
      // anyway); this top-level prop is the actual enforcement, kept alongside
      // the per-node flag as defense-in-depth.
      nodesDraggable={false}
      fitView
      fitViewOptions={FIT_VIEW_OPTIONS}
      minZoom={MIN_ZOOM}
      maxZoom={MAX_ZOOM}
      proOptions={{ hideAttribution: true }}
      defaultEdgeOptions={{ type: 'smoothstep' }}
      // Item 15 (mobile pan/pinch vs scroll-trap): gated on `interactive` (see
      // the state declaration above for the full rationale). When NOT
      // interactive (mobile, not yet tapped-to-activate) `panOnDrag` is false
      // and `.ig-touch-scroll` relaxes `touch-action` to `pan-y`, so a
      // one-finger drag scrolls the PAGE through the canvas instead of being
      // trapped. When interactive (desktop, fullscreen, or after the user taps
      // the on-canvas "pan & zoom" button) React Flow's native pan+pinch take
      // over with its default `touch-action:none`, which is what actually makes
      // two-finger pinch-zoom and drag-pan work — and keeps the zoom ON THE
      // GRAPH rather than leaking to the whole page.
      panOnDrag={interactive}
      zoomOnPinch={interactive}
    >
      <Controls showInteractive={false}>
        <ControlButton onClick={toggleFullscreen} title={isFullscreen ? 'Exit full screen' : 'Full screen'} aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'} data-testid="impact-graph-fullscreen">
          <ExpandIcon />
        </ControlButton>
      </Controls>
      {/* Mobile-only tap-to-activate gate (item 15). Hidden on desktop (always
          interactive) and in fullscreen (no page to scroll, so interaction is
          on automatically). A plain button — a tap fires its onClick
          regardless of the pane's gesture state, so activation is reliable. */}
      {isMobile && !isFullscreen && (
        <Panel position="top-center">
          {touchActive ? (
            <button
              type="button"
              className="ig-touch-gate ig-touch-gate--done"
              onClick={() => setTouchActive(false)}
              data-testid="impact-graph-touch-done"
            >
              ✓ Done — tap to scroll page
            </button>
          ) : (
            <button
              type="button"
              className="ig-touch-gate"
              onClick={() => setTouchActive(true)}
              data-testid="impact-graph-touch-activate"
            >
              ✥ Tap to pan &amp; zoom
            </button>
          )}
        </Panel>
      )}
      {nodes.length > MINIMAP_NODE_THRESHOLD && (
        <MiniMap
          pannable
          zoomable
          nodeColor={minimapNodeColor}
          maskColor="rgba(0,0,0,0.06)"
          style={isMobile ? MINIMAP_SIZE_MOBILE : MINIMAP_SIZE}
        />
      )}
    </ReactFlow>
  );
}

ImpactGraphCanvas.propTypes = {
  rfGraph: PropTypes.shape({ nodes: PropTypes.array.isRequired, edges: PropTypes.array.isRequired }).isRequired,
  reduceMotion: PropTypes.bool,
  isMobile: PropTypes.bool,
  fullscreenRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
};

// #182 polish round 2 (item 7): "parent"/"child" relationship types default to
// UNCHECKED — they're just a repeat of the tree view the item details panel
// already shows elsewhere, so surfacing them by default in the graph is
// redundant. Still toggleable back on; every OTHER type defaults to visible,
// unchanged.
const DEFAULT_HIDDEN_TYPES = new Set(['parent', 'child']);

function defaultVisibleTypes(allTypes) {
  return new Set(allTypes.filter((t) => !DEFAULT_HIDDEN_TYPES.has(t)));
}

const ImpactGraph = ({ tree, layoutMode: layoutModeProp, onLayoutModeChange, renderLayoutToggle = true }) => {
  const showHumanIds = usePref('showHumanIds');
  const reduceMotion = usePref('reduceMotion');
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const fullscreenRef = useRef(null);
  // #182 polish round 2 (item 6): titles and ids are now two INDEPENDENT
  // toggles (was one `showTitles` switch) — default is titles ON (shown in
  // FULL, not truncated — see impactGraphLayout.js's `estimateNodeWidth`) and
  // ids OFF.
  const [showTitles, setShowTitles] = useState(true);
  const [showIds, setShowIds] = useState(false);
  const [stackLeaves, setStackLeaves] = useState(true);
  // #238 follow-up: layout mode is controllable from the outside (ImpactTab
  // lifts this up so its own List/Graph toggle and this Linear/V-model toggle
  // can render in ONE row instead of two stacked ones — saves real vertical
  // space on mobile). Falls back to owning its own state when used standalone
  // (uncontrolled), so existing direct `<ImpactGraph tree={...}/>` usage/tests
  // are unaffected.
  const [internalLayoutMode, setInternalLayoutMode] = useState('family'); // 'family' | 'vmodel'
  const layoutMode = layoutModeProp !== undefined ? layoutModeProp : internalLayoutMode;
  const setLayoutMode = onLayoutModeChange || setInternalLayoutMode;

  const allTypes = useMemo(() => collectLinkTypes(tree), [tree]);
  const [visibleTypes, setVisibleTypes] = useState(() => defaultVisibleTypes(allTypes));

  // Reset the checkbox selection whenever a NEW tree is loaded (new root item /
  // re-fetch) — derive-during-render + reset pattern, so it's visible in the
  // same commit as the new tree (no flash of stale checkboxes).
  const allTypesKey = allTypes.join('|');
  const [resetKey, setResetKey] = useState(allTypesKey);
  if (resetKey !== allTypesKey) {
    setResetKey(allTypesKey);
    setVisibleTypes(defaultVisibleTypes(allTypes));
  }

  // These three memos used to key off the joined STRINGS (`allTypesKey`/`visibleTypesKey`)
  // rather than the arrays/Sets themselves, to avoid rebuilding when a new `tree` happened
  // to carry an identical type list. That saved nothing: `model` below depends on `tree`
  // directly, so ANY new tree already invalidates the whole chain
  // (model -> positioned -> rfGraph) regardless of what the type list did. Depending on
  // the real values is therefore behaviour-identical AND statically checkable.
  const palette = useMemo(() => {
    const m = new Map();
    allTypes.forEach((t, i) => m.set(t, EDGE_COLORS[i % EDGE_COLORS.length]));
    return m;
  }, [allTypes]);

  const model = useMemo(
    () => buildGraphModel(tree, visibleTypes, { stackLeaves }),
    [tree, visibleTypes, stackLeaves]
  );

  // #182: `linkTypes` (the tree's own ordered, deduped type list — the SAME
  // list the palette above is built from) drives the edge-handle slot
  // assignment (impactGraphLayout.js's `handleId`) so a type's connection
  // point stays consistent with its colour and, critically, never collides
  // with a DIFFERENT type's slot the way the old string-hash approach could.
  const positioned = useMemo(
    () => (layoutMode === 'family'
      ? layoutFamilyTree(model, { showTitles, showIds, linkTypes: allTypes })
      : layoutVModel(model, { showTitles, showIds, linkTypes: allTypes })),
    [model, layoutMode, showTitles, showIds, allTypes]
  );

  const rfGraph = useMemo(
    () => toReactFlowGraph(positioned, { palette, showHumanIds, showTitles, showIds }),
    [positioned, palette, showHumanIds, showTitles, showIds]
  );

  const toggleType = (type) => {
    setVisibleTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };

  return (
    <Box data-testid="impact-graph">
      {/* #238 / #182 follow-up: tight gaps on mobile. `FormControlLabel`
          defaults to a 16px RIGHT margin (on top of the flex `gap`), which the
          PO flagged as far too much space between the switches — `mr: 0` drops
          it so the flex `gap` alone controls spacing. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: { xs: 0.5, sm: 1 } }}>
        <FormControlLabel
          sx={{ mr: 0 }}
          control={(
            <Switch
              size="small"
              checked={showTitles}
              data-testid="impact-graph-toggle-titles"
              onChange={(e) => setShowTitles(e.target.checked)}
            />
          )}
          label="Show titles"
        />

        <FormControlLabel
          sx={{ mr: 0 }}
          control={(
            <Switch
              size="small"
              checked={showIds}
              data-testid="impact-graph-toggle-ids"
              onChange={(e) => setShowIds(e.target.checked)}
            />
          )}
          label="Show item numbers"
        />

        <FormControlLabel
          sx={{ mr: 0 }}
          control={(
            <Switch
              size="small"
              checked={stackLeaves}
              data-testid="impact-graph-toggle-stack"
              onChange={(e) => setStackLeaves(e.target.checked)}
            />
          )}
          label="Stack same-type siblings"
        />

        {renderLayoutToggle && (
          <ToggleButtonGroup
            size="small"
            value={layoutMode}
            exclusive
            onChange={(_e, v) => { if (v) setLayoutMode(v); }}
            aria-label="Graph layout"
          >
            <ToggleButton value="family" data-testid="impact-graph-layout-family" aria-label="Linear layout">
              Linear
            </ToggleButton>
            <ToggleButton value="vmodel" data-testid="impact-graph-layout-vmodel" aria-label="V-model layout">
              V-model
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Relationship-type filters. #182 follow-up: the PO flagged these as
          far too spaced-out — MUI's `FormControlLabel` defaults (16px right
          margin, -11px left margin) plus a 9px checkbox pad plus the row gap
          add up to a lot of dead space per chip. Tighten all three: `mr:0`
          (flex gap alone spaces them), `ml:0` (no negative pull), and a 4px
          checkbox pad. The label text also loses its 3px left gutter so the
          colour dot sits close to the checkbox. */}
      {allTypes.length > 0 && (
        <FormGroup row sx={{ mb: 1, columnGap: { xs: 1, sm: 1.5 }, rowGap: 0.25 }}>
          {allTypes.map((type) => (
            <FormControlLabel
              key={type}
              sx={{ mr: 0, ml: 0 }}
              control={(
                <Checkbox
                  size="small"
                  checked={visibleTypes.has(type)}
                  onChange={() => toggleType(type)}
                  slotProps={{ input: { 'data-testid': `impact-graph-filter-${type}` } }}
                  sx={{ p: '4px', color: palette.get(type), '&.Mui-checked': { color: palette.get(type) } }}
                />
              )}
              label={(
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <Box component="span" className="ig-legend-dot" sx={{ bgcolor: palette.get(type) }} />
                  {type}
                </Box>
              )}
            />
          ))}
        </FormGroup>
      )}

      <Box ref={fullscreenRef} className="impact-graph-canvas" data-testid="impact-graph-canvas">
        <ReactFlowProvider>
          <ImpactGraphCanvas rfGraph={rfGraph} reduceMotion={reduceMotion} isMobile={isMobile} fullscreenRef={fullscreenRef} />
        </ReactFlowProvider>
      </Box>
    </Box>
  );
};

ImpactGraph.propTypes = {
  tree: PropTypes.object,
  layoutMode: PropTypes.oneOf(['family', 'vmodel']),
  onLayoutModeChange: PropTypes.func,
  renderLayoutToggle: PropTypes.bool,
};

export default ImpactGraph;
